import { useCallback, useEffect, useState } from "react";
import { History } from "lucide-react";
import { ListEmptyState, ListTableLoading } from "../../../components/ListSearchFilters";
import { ListPagination } from "../../../components/ListPagination";
import { useAuth } from "../../../hooks/auth";
import { useToast } from "../../../hooks/toast";
import api from "../../../services/api";
import { fetchPropertyAudits } from "../../../services/propertyAudits";
import type { PropertyAuditEntry } from "../../../types/propertyAudit";
import refreshToken from "../../../utils/refreshToken";
import {
  PropertyAuditDesktopTable,
  PropertyAuditMobileList,
} from "./PropertyAuditViews";

type EditHistoryStepProps = {
  propertyId: number;
};

export default function EditHistoryStep({ propertyId }: EditHistoryStepProps) {
  const { access_token, dateToExpires } = useAuth();
  const { addToast } = useToast();

  const [audits, setAudits] = useState<PropertyAuditEntry[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const loadAudits = useCallback(
    async (page: number) => {
      if (!Number.isFinite(propertyId) || propertyId <= 0) return;

      setLoading(true);
      try {
        const token = await refreshToken(dateToExpires, access_token);
        api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;

        const result = await fetchPropertyAudits(api, propertyId, page);
        setAudits(result.audits);
        setCurrentPage(result.currentPage);
        setLastPage(result.lastPage);
        setTotal(result.total);
      } catch (err: unknown) {
        const message =
          (err as { response?: { data?: { message?: string } } })?.response?.data
            ?.message ?? "Não foi possível carregar o histórico de alterações.";

        addToast({ title: message, type: "error" });
        setAudits([]);
        setLastPage(0);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [access_token, addToast, dateToExpires, propertyId]
  );

  useEffect(() => {
    loadAudits(currentPage);
  }, [currentPage, loadAudits]);

  return (
    <section className="mx-auto w-full max-w-5xl min-w-0">
      <header className="mb-6 rounded-xl border border-slate-200 bg-gradient-to-r from-slate-50 to-white px-4 py-5 sm:px-6">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-orange-100 text-orange-600">
            <History className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h4 className="text-lg font-bold text-slate-800">
              Histórico de alterações
            </h4>
            <p className="mt-1 max-w-2xl text-sm text-slate-500">
              Registro de alterações de cadastro e eventos do ciclo de locação,
              com valores já formatados pela API.
            </p>
            {!loading && total > 0 ? (
              <p className="mt-2 text-xs font-medium text-slate-600">
                {total} {total === 1 ? "registro" : "registros"}
              </p>
            ) : null}
          </div>
        </div>
      </header>

      {loading ? (
        <ListTableLoading />
      ) : audits.length === 0 ? (
        <ListEmptyState message="Nenhuma alteração registrada para este imóvel ainda." />
      ) : (
        <>
          <PropertyAuditDesktopTable audits={audits} />
          <PropertyAuditMobileList audits={audits} />
          <ListPagination
            currentPage={currentPage}
            totalPages={lastPage}
            onPageChange={setCurrentPage}
          />
        </>
      )}
    </section>
  );
}
